import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { wrapSelection } from '@/lib/referral/epikrise';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const FIELD =
  'block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal disabled:cursor-not-allowed disabled:bg-ds-neutral-tint    ';

/**
 * Multi-line epikrise text with B / I / U (DIS01-S 4.10/4.19). The selection is
 * wrapped in [b]…[/b], [i]…[/i] or [u]…[/u]; Communicate writes <Bold>, <Italic>,
 * <Underline> and a <Break/> per line break. Markup cannot nest.
 */
export function EpikriseMarkupField({
  id,
  value,
  onChange,
  rows = 5,
  disabled = false,
  invalid = false,
  describedBy,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  rows?: number;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);

  function wrap(tag: 'b' | 'i' | 'u') {
    const el = ref.current;
    if (!el || disabled) return;
    const result = wrapSelection(value, el.selectionStart, el.selectionEnd, tag);
    if (!result.changed) return;
    onChange(result.text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.start, result.end);
    });
  }

  const buttons: Array<{ tag: 'b' | 'i' | 'u'; label: string; title: string; className: string }> = [
    { tag: 'b', label: 'B', title: t('referrals.epikrise_bold', 'Fed'), className: 'font-bold' },
    { tag: 'i', label: 'I', title: t('referrals.epikrise_italic', 'Kursiv'), className: 'italic' },
    { tag: 'u', label: 'U', title: t('referrals.epikrise_underline', 'Understreget'), className: 'underline' },
  ];

  return (
    <div className="mt-1">
      <div className="mb-1 flex gap-1" role="toolbar" aria-label={t('referrals.epikrise_format_toolbar', 'Fremhævning')}>
        {buttons.map((b) => (
          <button
            key={b.tag}
            type="button"
            title={b.title}
            aria-label={b.title}
            data-testid={`${id}-${b.tag}`}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => wrap(b.tag)}
            className={cn(
              'h-7 w-7 rounded border border-ds-field text-sm text-ds-text hover:bg-ds-row-hover disabled:opacity-40   ',
              b.className,
            )}
          >
            {b.label}
          </button>
        ))}
      </div>
      <textarea
        id={id}
        ref={ref}
        rows={rows}
        value={value}
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        className={cn(TYPE.inputValue, FIELD)}
      />
    </div>
  );
}
